(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
  root.EvSearchUtils = api;
  if (root.document) {
    api.boot(root);
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 300;
  var QUERY_MAX_LEN = 64;
  var OPEN_CLASS = 'active';
  var SHOW_CLASS = 'show';
  var BODY_CLASS = 'active_search';

  function debounce(fn, wait) {
    var timer;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function sanitizeQuery(value) {
    if (value == null) return '';
    var text = String(value)
      .replace(/[\u0000-\u001F\u007F]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!text) return '';
    if (text.length > QUERY_MAX_LEN) text = text.slice(0, QUERY_MAX_LEN);
    return text;
  }

  /**
   * Analytics-only redaction. Does not change what the customer can type or submit.
   * Omits email-like and phone-like sequences even when embedded in a longer string.
   */
  function containsAnalyticsPii(value) {
    if (value == null) return false;
    var text = String(value);
    if (/[^\s@]+@[^\s@]+\.[^\s@]+/.test(text) || /@/.test(text)) return true;
    var match;
    var phoneRe = /\+?\d[\d\s\-()]{5,}\d/g;
    while ((match = phoneRe.exec(text))) {
      if (match[0].replace(/\D/g, '').length >= 7) return true;
    }
    return false;
  }

  function analyticsQuery(value) {
    if (containsAnalyticsPii(value)) return undefined;
    var clean = sanitizeQuery(value);
    if (!clean) return undefined;
    return clean.toLowerCase();
  }

  function pushEvent(name, payload) {
    try {
      var win =
        typeof globalThis !== 'undefined'
          ? globalThis
          : typeof window !== 'undefined'
            ? window
            : null;
      if (!win) return;
      win.dataLayer = win.dataLayer || [];
      win.dataLayer.push(Object.assign({ event: name }, payload || {}));
    } catch (err) {
      /* ignore analytics failures */
    }
  }

  function viewportLabel() {
    if (typeof window === 'undefined' || !window.matchMedia) return 'desktop';
    return window.matchMedia('(max-width: 1199px)').matches ? 'mobile' : 'desktop';
  }

  function isExcludedAncestor(node) {
    if (!node || node.nodeType !== 1) return false;
    if (node.hasAttribute('hidden')) return true;
    if (node.inert === true) return true;
    if (node.getAttribute('aria-hidden') === 'true') return true;
    return false;
  }

  function isFocusable(el, root) {
    if (!el || el.nodeType !== 1) return false;
    if (el.getAttribute('tabindex') === '-1') return false;
    if (el.disabled) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    if (el.hasAttribute('hidden')) return false;

    var node = el;
    while (node && node !== document.documentElement) {
      if (isExcludedAncestor(node)) return false;
      var style = window.getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      if (node === root) break;
      node = node.parentElement;
    }
    return true;
  }

  function getFocusable(container) {
    if (!container) return [];
    var nodes = container.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    return Array.prototype.filter.call(nodes, function (el) {
      return isFocusable(el, container);
    });
  }

  /**
   * Promote lazysizes data-* images (and any missing src) so injected markup
   * paints deterministically without waiting for an IntersectionObserver pass.
   */
  function hydrateImages(root) {
    if (!root || !root.querySelectorAll) return;
    var images = root.querySelectorAll('img');
    Array.prototype.forEach.call(images, function (img) {
      var dataSrc = img.getAttribute('data-src');
      var dataSrcset = img.getAttribute('data-srcset');
      if (dataSrc && !img.getAttribute('src')) {
        img.setAttribute('src', dataSrc);
      }
      if (dataSrcset && !img.getAttribute('srcset')) {
        img.setAttribute('srcset', dataSrcset);
      }
      if (img.getAttribute('data-sizes') && !img.getAttribute('sizes')) {
        img.setAttribute('sizes', img.getAttribute('data-sizes') === 'auto' ? '100vw' : img.getAttribute('data-sizes'));
      }
      img.setAttribute('loading', 'eager');
      img.classList.remove('lazyload', 'lazyloading', 'fade-in');
      img.classList.add('lazyloaded');
      img.style.opacity = '1';
      if (window.lazySizes && typeof window.lazySizes.loader === 'object' && typeof window.lazySizes.loader.unveil === 'function') {
        try {
          window.lazySizes.loader.unveil(img);
        } catch (err) {
          /* ignore unveil failures */
        }
      }
    });
  }

  function EvSearch(doc) {
    doc = doc || document;
    this.document = doc;
    this.overlay = doc.querySelector('[data-ev-search-overlay]');
    if (!this.overlay) return;

    // Ensure the overlay is not trapped inside a display:none header branch
    // (e.g. .header-desktop on mobile), which collapses fixed positioning.
    if (this.overlay.parentElement !== doc.body) {
      doc.body.appendChild(this.overlay);
    }

    this.form = this.overlay.querySelector('[data-ev-search-form]');
    this.input = this.overlay.querySelector('[data-ev-search-input]');
    this.results = this.overlay.querySelector('[data-ev-search-results]');
    this.resultsPanel = this.overlay.querySelector('[data-ev-search-results-panel]');
    this.discoveryPanel = this.overlay.querySelector('[data-ev-search-discovery-panel]');
    this.status = this.overlay.querySelector('[data-ev-search-status]');
    this.live = this.overlay.querySelector('[data-ev-search-live]');
    this.root = this.overlay.querySelector('[data-ev-search-root]');
    this.loadingText = (this.root && this.root.getAttribute('data-loading-text')) || 'Loading results';
    this.errorText = (this.root && this.root.getAttribute('data-error-text')) || 'Unable to load results. Please try again.';
    this.suggestedText =
      (this.root && this.root.getAttribute('data-suggested-text')) || 'Suggested products';
    this.lastToggle = null;
    this.abortController = null;
    this.requestId = 0;
    this.isOpen = false;
    this._bgState = null;

    this.onInput = debounce(this.handleInput.bind(this), DEBOUNCE_MS);
    this.bind();
    if (this.resultsPanel && this.resultsPanel.hidden && 'inert' in this.resultsPanel) {
      this.resultsPanel.inert = true;
    }
  }

  /**
   * Make storefront content outside the overlay unavailable to keyboard / AT while
   * open. Never marks the overlay (or an ancestor that contains it) as inert/hidden.
   * Preserves and restores prior inert / aria-hidden on each affected node.
   */
  EvSearch.prototype.suppressBackground = function () {
    if (!this.overlay || this._bgState) return;
    var doc = this.document;
    var overlay = this.overlay;
    var states = [];
    var children = doc.body ? doc.body.children : [];

    Array.prototype.forEach.call(children, function (el) {
      if (!el || el.nodeType !== 1) return;
      if (el === overlay) return;
      if (overlay.contains(el)) return;
      if (el.contains && el.contains(overlay)) return;

      var tag = el.tagName;
      if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'LINK' || tag === 'TEMPLATE' || tag === 'NOSCRIPT' || tag === 'META') {
        return;
      }

      var id = el.id || '';
      // Leave Shopify preview/admin chrome alone so it is not permanently altered.
      if (
        id === 'preview-bar-iframe' ||
        id === 'PreviewFrame' ||
        id === 'admin-bar-iframe' ||
        id === 'shopify-developer-bar' ||
        /^shopify-preview/i.test(id) ||
        /^PBar/i.test(id)
      ) {
        return;
      }

      var prevInert = 'inert' in el ? el.inert : null;
      var hadAriaHidden = el.hasAttribute('aria-hidden');
      var prevAriaHidden = hadAriaHidden ? el.getAttribute('aria-hidden') : null;

      states.push({
        el: el,
        inert: prevInert,
        hadAriaHidden: hadAriaHidden,
        ariaHidden: prevAriaHidden,
        usedInert: 'inert' in el
      });

      if ('inert' in el) {
        el.inert = true;
      } else {
        el.setAttribute('aria-hidden', 'true');
      }
    });

    this._bgState = states;
  };

  EvSearch.prototype.restoreBackground = function () {
    var states = this._bgState;
    if (!states) return;

    Array.prototype.forEach.call(states, function (prev) {
      var el = prev.el;
      if (!el) return;

      if (prev.usedInert && 'inert' in el) {
        el.inert = !!prev.inert;
      }

      if (prev.hadAriaHidden) {
        if (prev.ariaHidden == null) el.removeAttribute('aria-hidden');
        else el.setAttribute('aria-hidden', prev.ariaHidden);
      } else if (!prev.usedInert) {
        el.removeAttribute('aria-hidden');
      }
      // When inert was used, do not leave a residual aria-hidden we did not set.
    });

    this._bgState = null;
  };

  EvSearch.prototype.bind = function () {
    var self = this;
    var doc = this.document;

    doc.addEventListener('click', function (event) {
      var openBtn = event.target.closest('.search-toggle, [data-ev-search-open]');
      if (openBtn && !openBtn.closest('[data-ev-search-overlay]')) {
        event.preventDefault();
        self.open(openBtn);
        return;
      }

      var closeBtn = event.target.closest('[data-ev-search-close]');
      if (closeBtn) {
        event.preventDefault();
        self.close();
        return;
      }

      var suggestion = event.target.closest('[data-ev-search-suggestion]');
      if (suggestion && self.overlay.contains(suggestion)) {
        event.preventDefault();
        self.applySuggestion(suggestion);
        return;
      }

      var product = event.target.closest('[data-ev-search-product]');
      if (product && self.overlay.contains(product)) {
        pushEvent('ev_search_product_click', {
          product_id: product.getAttribute('data-product-id') || undefined,
          product_handle: product.getAttribute('data-product-handle') || undefined,
          list: product.getAttribute('data-list') || undefined,
          position: Number(product.getAttribute('data-position') || 0) || undefined,
          query: analyticsQuery(self.input && self.input.value),
          viewport: viewportLabel()
        });
      }
    });

    doc.addEventListener('keydown', function (event) {
      var openBtn = event.target.closest('.search-toggle, [data-ev-search-open]');
      if (openBtn && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        self.open(openBtn);
        return;
      }

      if (!self.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        self.close();
        return;
      }

      if (event.key === 'Tab') {
        self.trapFocus(event);
      }
    });

    if (this.input) {
      this.input.addEventListener('input', this.onInput);
    }

    if (this.form) {
      this.form.addEventListener('submit', function (event) {
        var query = self.getQuery();
        if (!query) {
          event.preventDefault();
          return;
        }
        pushEvent('ev_search_submit', {
          query: analyticsQuery(query),
          source: 'overlay',
          viewport: viewportLabel()
        });
      });
    }
  };

  EvSearch.prototype.getQuery = function () {
    return this.input ? this.input.value.trim() : '';
  };

  EvSearch.prototype.open = function (toggle) {
    if (!this.overlay) return;
    this.lastToggle = toggle || this.lastToggle || this.document.querySelector('.search-toggle');
    if (this.isOpen) {
      if (this.input) this.input.focus({ preventScroll: true });
      return;
    }
    this.overlay.hidden = false;
    this.overlay.classList.add(OPEN_CLASS);
    this.document.body.classList.add(BODY_CLASS);
    this.document.body.classList.remove('menu-mobile--open');
    var topbar = this.document.querySelector('.bwp-header-topbar');
    if (topbar) topbar.classList.add('active-index');

    this.setToggleExpanded(true);
    this.suppressBackground();

    var self = this;
    window.requestAnimationFrame(function () {
      self.overlay.classList.add(SHOW_CLASS);
      if (self.input) {
        self.input.focus({ preventScroll: true });
      }
      // Retain current results-page query and refresh predictive when safe.
      var retained = self.getQuery();
      if (retained.length >= MIN_CHARS) {
        self.handleInput();
      } else {
        self.showDiscovery();
      }
    });

    this.isOpen = true;
    hydrateImages(this.overlay);
    pushEvent('ev_search_open', {
      location: this.document.body.getAttribute('class') || undefined,
      viewport: viewportLabel()
    });
  };

  EvSearch.prototype.close = function () {
    if (!this.overlay || !this.isOpen) return;
    var self = this;
    this.overlay.classList.remove(SHOW_CLASS);
    this.document.body.classList.remove(BODY_CLASS);
    var topbar = this.document.querySelector('.bwp-header-topbar');
    if (topbar) topbar.classList.remove('active-index');
    this.setToggleExpanded(false);

    this.isOpen = false;
    this.showDiscovery();
    this.restoreBackground();

    window.setTimeout(function () {
      self.overlay.classList.remove(OPEN_CLASS);
      self.overlay.hidden = true;
    }, 300);

    if (this.lastToggle && typeof this.lastToggle.focus === 'function') {
      this.lastToggle.focus({ preventScroll: true });
    }
  };

  EvSearch.prototype.setToggleExpanded = function (expanded) {
    var toggles = this.document.querySelectorAll('.search-toggle, [data-ev-search-open]');
    Array.prototype.forEach.call(toggles, function (toggle) {
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      toggle.setAttribute('aria-controls', 'ev-search-overlay');
    });
  };

  EvSearch.prototype.setInputExpanded = function () {
    // Intentionally empty: aria-expanded is invalid on a plain text input
    // without combobox semantics. Toggle buttons own aria-expanded instead.
  };

  EvSearch.prototype.trapFocus = function (event) {
    var focusable = getFocusable(this.overlay);
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  EvSearch.prototype.applySuggestion = function (button) {
    var query = button.getAttribute('data-query') || button.getAttribute('data-label') || '';
    pushEvent('ev_search_suggestion_click', {
      label: sanitizeQuery(button.getAttribute('data-label') || ''),
      query: analyticsQuery(query),
      position: Number(button.getAttribute('data-position') || 0) || undefined,
      viewport: viewportLabel()
    });
    if (!this.input) return;
    this.input.value = query;
    this.input.focus({ preventScroll: true });
    this.handleInput();
  };

  EvSearch.prototype.handleInput = function () {
    var query = this.getQuery();
    if (!query.length || query.length < MIN_CHARS) {
      this.showDiscovery();
      return;
    }
    this.fetchResults(query);
  };

  EvSearch.prototype.showDiscovery = function () {
    this.cancelRequest();
    if (this.discoveryPanel) {
      this.discoveryPanel.hidden = false;
      if ('inert' in this.discoveryPanel) this.discoveryPanel.inert = false;
    }
    if (this.resultsPanel) {
      this.resultsPanel.hidden = true;
      if ('inert' in this.resultsPanel) this.resultsPanel.inert = true;
    }
    if (this.results) this.results.innerHTML = '';
    if (this.overlay) this.overlay.classList.remove('ev-search--empty');
    this.setStatus('');
    this.setInputExpanded(false);
    hydrateImages(this.discoveryPanel || this.overlay);
  };

  EvSearch.prototype.showResultsPanel = function (opts) {
    opts = opts || {};
    var keepDiscovery = !!opts.keepDiscovery;
    if (this.discoveryPanel) {
      this.discoveryPanel.hidden = !keepDiscovery;
      if ('inert' in this.discoveryPanel) this.discoveryPanel.inert = !keepDiscovery;
    }
    if (this.resultsPanel) {
      this.resultsPanel.hidden = false;
      if ('inert' in this.resultsPanel) this.resultsPanel.inert = false;
    }
    if (this.overlay) {
      if (keepDiscovery) this.overlay.classList.add('ev-search--empty');
      else this.overlay.classList.remove('ev-search--empty');
    }
  };

  EvSearch.prototype.formatResultsCount = function (count) {
    return this.suggestedText || 'Suggested products';
  };

  EvSearch.prototype.setStatus = function (text) {
    if (this.status) this.status.textContent = text || '';
    if (this.live) {
      this.live.textContent = text || '';
      this.live.setAttribute('aria-hidden', text ? 'false' : 'true');
    }
  };

  /**
   * Abort in-flight fetch and invalidate the active request generation so
   * late responses cannot render after clear / shorten / close.
   */
  EvSearch.prototype.cancelRequest = function () {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.requestId += 1;
  };

  EvSearch.prototype.fetchResults = function (query) {
    var self = this;
    var routes = (typeof window !== 'undefined' && window.routes) || {};
    var endpoint = routes.predictive_search_url || '/search/suggest';

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    var requestId = ++this.requestId;
    this.abortController = typeof AbortController === 'function' ? new AbortController() : null;

    this.showResultsPanel({ keepDiscovery: false });
    this.setStatus(this.loadingText);
    this.setInputExpanded(true);
    if (this.results) {
      this.results.innerHTML = '<div class="ev-search-loading" aria-hidden="true"></div>';
    }

    var limit = 8;
    try {
      if (typeof window !== 'undefined' && window.theme && window.theme.searchItemCount) {
        limit = Number(window.theme.searchItemCount) || 8;
      }
    } catch (err) {
      limit = 8;
    }
    if (limit < 1) limit = 8;
    if (limit > 10) limit = 10;

    // Align with storefront search product scope. options[prefix]=last on the
    // submit form enables last-term partial matches on /search; predictive
    // search already applies last-term partial matching natively and has no
    // equivalent prefix param. Expand fields slightly beyond the default set.
    var url =
      endpoint +
      '?q=' +
      encodeURIComponent(query) +
      '&' +
      encodeURIComponent('resources[type]') +
      '=product&' +
      encodeURIComponent('resources[limit]') +
      '=' +
      encodeURIComponent(String(limit)) +
      '&' +
      encodeURIComponent('resources[options][unavailable_products]') +
      '=last&' +
      encodeURIComponent('resources[options][fields]') +
      '=' +
      encodeURIComponent('title,product_type,variants.title,vendor,tag') +
      '&section_id=predictive-search';

    var fetchOpts = this.abortController ? { signal: this.abortController.signal } : {};

    fetch(url, fetchOpts)
      .then(function (response) {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then(function (text) {
        if (requestId !== self.requestId) return;
        if (!self.isOpen) return;
        if (self.getQuery().length < MIN_CHARS) return;

        var parsed = new DOMParser().parseFromString(text, 'text/html');
        var markupNode = parsed.querySelector('#predictive-search-product');
        if (!markupNode) throw new Error('empty-section');
        if (self.results) self.results.innerHTML = markupNode.innerHTML;
        hydrateImages(self.results);

        var count = markupNode.querySelectorAll('[data-ev-search-product]').length;
        if (!count) {
          self.showResultsPanel({ keepDiscovery: true });
          hydrateImages(self.discoveryPanel);
          self.setStatus('');
          pushEvent('ev_search_no_results', {
            query: analyticsQuery(query),
            context: 'predictive',
            viewport: viewportLabel()
          });
        } else {
          self.showResultsPanel({ keepDiscovery: false });
          self.setStatus(self.formatResultsCount(count));
        }
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        if (requestId !== self.requestId) return;
        if (!self.isOpen) return;
        if (self.results) {
          self.results.innerHTML =
            '<p class="ev-search-results__error" role="alert">' + self.errorText + '</p>';
        }
        self.showResultsPanel({ keepDiscovery: true });
        hydrateImages(self.discoveryPanel);
        self.setStatus(self.errorText);
      });
  };

  function emitPageNoResultsOnce(doc) {
    doc = doc || (typeof document !== 'undefined' ? document : null);
    if (!doc) return false;
    var node = doc.querySelector('[data-ev-search-page-no-results]');
    if (!node || node.getAttribute('data-emitted') === '1') return false;
    var payload = {};
    try {
      payload = JSON.parse(node.textContent || '{}') || {};
    } catch (err) {
      payload = {};
    }
    node.setAttribute('data-emitted', '1');
    pushEvent('ev_search_no_results', {
      query: analyticsQuery(payload.query),
      context: payload.context || 'results',
      viewport: viewportLabel()
    });
    return true;
  }

  function boot(win) {
    win = win || window;
    if (win.evSearch && win.evSearch.__booted) return win.evSearch;
    win.evSearch = new EvSearch(win.document);
    win.evSearch.__booted = true;
    emitPageNoResultsOnce(win.document);
    return win.evSearch;
  }

  return {
    MIN_CHARS: MIN_CHARS,
    DEBOUNCE_MS: DEBOUNCE_MS,
    QUERY_MAX_LEN: QUERY_MAX_LEN,
    sanitizeQuery: sanitizeQuery,
    containsAnalyticsPii: containsAnalyticsPii,
    analyticsQuery: analyticsQuery,
    isExcludedAncestor: isExcludedAncestor,
    isFocusable: isFocusable,
    getFocusable: getFocusable,
    hydrateImages: hydrateImages,
    emitPageNoResultsOnce: emitPageNoResultsOnce,
    EvSearch: EvSearch,
    boot: boot
  };
});
